import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { DrawerWithCloseButton } from '@/components/ui/drawer'
import { ChatIndicationDetailsContent } from '../../../../components/Chat/ChatIndicationDetailsContent'
import { ChatInsuranceList } from '../../../../components/Chat/ChatInsuranceList'
import { IndicationDetailActions } from '../../../../components/Chat/IndicationDetailActions'
import { CacheKeys } from '../../../../lib/cache'
import { parseError } from '../../../../lib/errors'
import { cn } from '../../../../lib/utils'
import { useFeatureFlag } from '../../../../models/flag'
import { PortalLink } from '../../../../models/insuranceApplication'
import {
  getActiveQuote,
  InsuranceIndication as InsuranceIndicationType,
} from '../../../../models/insuranceIndication'
import { InsuranceQuote, requestToBindQuote } from '../../../../models/insuranceQuote'
import { InsuranceIndicationStatus, InsuranceQuoteStatus } from '../../../../models/insuranceTypes'
import { User } from '../../../../models/user'

interface InsuranceIndicationsProps {
  indications: InsuranceIndicationType[]
  user?: User
  renderIndicationChildren?: (indication: InsuranceIndicationType) => React.ReactNode
  /** CTAs for one indication, rendered in the details sidebar rather than on
   * the card — the card is for reading the numbers. */
  renderDetailActions?: (indication: InsuranceIndicationType) => React.ReactNode
  /** Page-specific links for the sidebar, grouped with the shared ones. */
  renderDetailLinks?: (indication: InsuranceIndicationType) => React.ReactNode
  hideUserCta?: boolean
  hideCheckbox?: boolean
  portalLinks?: PortalLink[]
  /** Defaults to the passed user's flag; pass explicitly where `user` isn't threaded through. */
  isCaiAdmin?: boolean
  /** Needed for the card's Resubmit button — omitted, it doesn't render. */
  applicationId?: string
  /** Needed for the card's "Send to Corgi" action — omitted, it doesn't render. */
  orgId?: string
  /** Overrides the default chat-column width. Staff pages have the room to run wider. */
  className?: string
  /** Staff only: when the application last changed, to flag a Corgi draft with unsent edits. */
  applicationEditedAt?: string | null
}

export function InsuranceIndications({
  indications,
  user,
  renderIndicationChildren,
  renderDetailActions,
  renderDetailLinks,
  hideUserCta,
  hideCheckbox,
  portalLinks,
  isCaiAdmin,
  applicationId,
  orgId,
  className,
  applicationEditedAt,
}: InsuranceIndicationsProps) {
  const queryClient = useQueryClient()
  const canBindQuote = useFeatureFlag('ENABLE_BIND_QUOTE')
  const [selectedDetail, setSelectedDetail] = useState<InsuranceIndicationType | InsuranceQuote | null>(null)
  // The detail on show may be the active quote, so the indication it belongs to
  // is tracked alongside it — the actions below all act on the indication.
  const [detailIndication, setDetailIndication] = useState<InsuranceIndicationType | null>(null)
  const [selectedIndication, setSelectedIndication] = useState<InsuranceIndicationType | null>(null)
  const hasQuotes = indications.some((i) => i.quotes?.length > 0)
  const canContinue =
    canBindQuote &&
    indications.some((indication) => {
      const activeQuote = getActiveQuote(indication)
      return (
        indication.status === InsuranceIndicationStatus.GENERATED &&
        activeQuote?.status !== InsuranceQuoteStatus.REQUEST_TO_BIND
      )
    })

  function handleToggleSelect(indication: InsuranceIndicationType) {
    if (selectedIndication?.id === indication.id) {
      setSelectedIndication(null)
    } else {
      setSelectedIndication(indication)
    }
  }

  const handleRequestToBind = async () => {
    const quote = selectedIndication ? getActiveQuote(selectedIndication) : null
    const orgId = user?.currentOrg?.id
    if (!orgId || !quote) return
    try {
      await requestToBindQuote(orgId, quote)
      queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS, orgId, 'application'] })
    } catch (err) {
      toast.error(parseError(err))
    }
  }

  return (
    <>
      <div className={cn('flex max-w-3xl flex-col gap-4', className)}>
        <ChatInsuranceList
          indications={indications}
          isStaff={user?.isStaff}
          isCaiAdmin={isCaiAdmin ?? user?.isCaiAdmin}
          applicationId={applicationId}
          orgId={orgId}
          applicationEditedAt={applicationEditedAt}
          handleToggleSelect={handleToggleSelect}
          selected={selectedIndication ? [selectedIndication] : []}
          showQuotes={hasQuotes}
          onViewDetails={(detail, indication) => {
            setSelectedDetail(detail)
            setDetailIndication(indication)
          }}
          renderIndicationChildren={renderIndicationChildren}
          hideCheckbox={hideCheckbox}
          portalLinks={portalLinks}
        />
        {canContinue && !hideUserCta && (
          <div className="flex justify-end">
            <Button variant="outline" size="sm" disabled={!selectedIndication} onClick={handleRequestToBind}>
              Request to bind
            </Button>
          </div>
        )}
      </div>
      <DrawerWithCloseButton
        title="Details"
        open={!!selectedDetail}
        onOpenChange={(open) => {
          if (!open) setSelectedDetail(null)
        }}
        handleClose={() => setSelectedDetail(null)}
      >
        {selectedDetail && (
          <ChatIndicationDetailsContent
            indication={selectedDetail}
            actions={
              detailIndication && (
                <IndicationDetailActions
                  indication={detailIndication}
                  isStaff={user?.isStaff}
                  isCaiAdmin={isCaiAdmin ?? user?.isCaiAdmin}
                  links={renderDetailLinks?.(detailIndication)}
                >
                  {renderDetailActions?.(detailIndication)}
                </IndicationDetailActions>
              )
            }
          />
        )}
      </DrawerWithCloseButton>
    </>
  )
}
